"use client";
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import useLocalStorage from "hooks/useLocalStorage";
import { AppI18nContext, AppI18nProps, Locale } from "types/provider/I18n";
import viMessages from "i18n/locales/vi.json";
import enMessages from "i18n/locales/en.json";

const MESSAGES: Record<Locale, Record<string, any>> = {
  vi: viMessages,
  en: enMessages,
};

const STORAGE_KEY = "@lookme::locale";
const DEFAULT_LOCALE: Locale = "vi";

const isLocale = (v: any): v is Locale => v === "vi" || v === "en";

export const I18nContext = createContext<AppI18nContext>({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: (key: string) => key,
});

// Lấy giá trị lồng nhau theo key dạng "a.b.c"
function getNested(obj: Record<string, any>, path: string): string | undefined {
  return path
    .split(".")
    .reduce<any>((acc, part) => (acc == null ? undefined : acc[part]), obj);
}

const I18nProvider = ({ children, locale: localeFromUrl }: AppI18nProps) => {
  // Ghi nhớ lựa chọn gần nhất để lần sau vào thẳng /en nếu muốn (không bắt buộc).
  const [, $savedLocale] = useLocalStorage(STORAGE_KEY, DEFAULT_LOCALE);

  // Ngôn ngữ giờ do URL quyết định (server truyền xuống từ app/[locale]),
  // nhờ vậy nội dung đúng ngôn ngữ nằm sẵn trong HTML → Google đọc được,
  // và không còn nguy cơ lệch giữa HTML server và lần render đầu ở client.
  const [locale, setLocaleState] = useState<Locale>(
    isLocale(localeFromUrl) ? localeFromUrl : DEFAULT_LOCALE,
  );

  useEffect(() => {
    if (isLocale(localeFromUrl)) setLocaleState(localeFromUrl);
  }, [localeFromUrl]);

  // Điều hướng sang URL của ngôn ngữ kia do LanguageSwitcher lo; hàm này chỉ
  // giữ lại để tương thích và để ghi nhớ lựa chọn.
  const setLocale = useCallback(
    (next: Locale) => {
      setLocaleState(next);
      $savedLocale(next);
    },
    [$savedLocale],
  );

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      const value =
        getNested(MESSAGES[locale], key) ??
        getNested(MESSAGES[DEFAULT_LOCALE], key);
      const str = typeof value === "string" ? value : key;
      if (!params) return str;
      // Thay {name} bằng params.name
      return str.replace(/\{(\w+)\}/g, (m, name) =>
        params[name] !== undefined ? String(params[name]) : m,
      );
    },
    [locale],
  );

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);

// Hook tiện dụng: const t = useT();  t("home.bookNow")
export const useT = () => useContext(I18nContext).t;

export default I18nProvider;
